/**
 * Format a number as USD currency.
 *
 * @param {number} amount - Amount to format
 * @param {boolean} [inCents=true] - Whether amount is in cents (true) or dollars (false)
 * @returns {string} Formatted currency string (e.g., "$1,234.56")
 *
 * @example
 * currency(123456) // "$1,234.56"
 * currency(123.45, false) // "$123.45"
 */
export function currency(amount, inCents = true) {
    const USDollar = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    })

    return USDollar.format(inCents ? amount / 100 : amount)
}

/**
 * Format a number with thousand separators.
 *
 * @param {number} number - Number to format
 * @returns {string} Formatted number string (e.g., "1,234,567")
 *
 * @example
 * number(1234567) // "1,234,567"
 */
export function number(number) {
    return new Intl.NumberFormat().format(number)
}

/**
 * Default character budget for a json flag value shown inline (target
 * pickers, target tables, history). Long JSON documents are cut here and the
 * full value is exposed via a title tooltip instead.
 */
export const JSON_DISPLAY_MAX_LENGTH = 80

/**
 * Truncate a display string to a maximum length, appending an ellipsis when
 * cut. Non-string values (e.g. an already-decoded JSON object) are
 * stringified first so callers can pass a raw value.
 *
 * @param {*} value - Value to display
 * @param {number} [maxLength=JSON_DISPLAY_MAX_LENGTH] - Maximum length, including the ellipsis
 * @returns {string} The display string, truncated if needed
 *
 * @example
 * truncateDisplay('{"a":1}') // '{"a":1}'
 * truncateDisplay('abcdefgh', 5) // 'abcd…'
 */
export function truncateDisplay(value, maxLength = JSON_DISPLAY_MAX_LENGTH) {
    if (value === null || value === undefined) {
        return ''
    }

    let display = value
    if (typeof value !== 'string') {
        try {
            display = JSON.stringify(value) ?? String(value)
        } catch {
            display = String(value)
        }
    }

    if (display.length <= maxLength) {
        return display
    }

    return display.slice(0, Math.max(maxLength - 1, 0)) + '…'
}
